import { useEffect } from "react";

/**
 * Minimal head manager without pulling in react-helmet.
 *
 * Sets document.title, the meta description, canonical link, and
 * optionally appends a JSON-LD script. Cleans up on unmount so pages
 * don't leak metadata into each other.
 */
export default function PageHead({
  title,
  description,
  canonical,
  jsonLd,
  robots,
}) {
  useEffect(() => {
    const prev = { title: document.title };

    if (title) document.title = title;

    let descTag = document.querySelector('meta[name="description"]');
    let hadDesc = !!descTag;
    let prevDesc = descTag?.getAttribute("content");
    if (description) {
      if (!descTag) {
        descTag = document.createElement("meta");
        descTag.setAttribute("name", "description");
        document.head.appendChild(descTag);
      }
      descTag.setAttribute("content", description);
    }

    let robotsTag = document.querySelector('meta[name="robots"]');
    let hadRobots = !!robotsTag;
    let prevRobots = robotsTag?.getAttribute("content");
    if (robots) {
      if (!robotsTag) {
        robotsTag = document.createElement("meta");
        robotsTag.setAttribute("name", "robots");
        document.head.appendChild(robotsTag);
      }
      robotsTag.setAttribute("content", robots);
    }

    let canonTag = document.querySelector('link[rel="canonical"]');
    let prevCanon = canonTag?.getAttribute("href");
    if (canonical) {
      if (!canonTag) {
        canonTag = document.createElement("link");
        canonTag.setAttribute("rel", "canonical");
        document.head.appendChild(canonTag);
      }
      canonTag.setAttribute("href", canonical);
    }

    let script;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.pageHead = "1";
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      if (prev.title) document.title = prev.title;
      if (description && descTag) {
        if (hadDesc && prevDesc !== undefined) descTag.setAttribute("content", prevDesc);
      }
      if (robots && robotsTag) {
        if (hadRobots && prevRobots !== undefined) robotsTag.setAttribute("content", prevRobots);
        else if (!hadRobots && robotsTag.parentNode) robotsTag.parentNode.removeChild(robotsTag);
      }
      if (canonical && canonTag && prevCanon !== undefined) {
        canonTag.setAttribute("href", prevCanon);
      }
      if (script && script.parentNode) script.parentNode.removeChild(script);
    };
  }, [title, description, canonical, jsonLd, robots]);

  return null;
}
